import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";

// Picks the best available localized name for UI labels.
export function pickStartPointTourName(tour) {
  const names =
    tour?.name?.en.value || Object.values(tour?.name || {})[0]?.value;
  return names || "";
}

export function normalizeStartPointHtmlValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

export function normalizeStartPointLocalizedValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

export function resolveStartPointTourId(tour) {
  // Supports API variants where the same identifier uses different keys.
  return tour?.id || tour?.tur_id || tour?.tourId || tour?.code || "";
}

// Normalizes language payload from API/store into UI-friendly shape.
export function buildStartPointLanguageList(languages = []) {
  return languages
    .map((language) => ({
      id: language.lng_id || language.id,
      iso: (language.lng_iso || language.iso || "").toLowerCase(),
      name: language.lng_name || language.name || "",
    }))
    .filter((language) => language.iso);
}

// Flattens localized description values before sending to API.
export function buildStartPointDescriptionPayload(
  description = {},
  languageList = [],
) {
  return (languageList || []).reduce((acc, language) => {
    const iso = language?.iso;
    if (!iso) return acc;
    acc[iso] = String(normalizeStartPointHtmlValue(description?.[iso]) ?? "");
    return acc;
  }, {});
}

export function buildStartPointDescriptionTranslationPayload(
  description = {},
  languageList = [],
) {
  return (languageList || []).reduce((acc, language) => {
    const iso = language?.iso;
    if (!iso) return acc;
    const source = description?.[iso];
    acc[iso] = {
      id: source && typeof source === "object" ? source.id || "" : "",
      value: String(normalizeStartPointHtmlValue(source) ?? ""),
    };
    return acc;
  }, {});
}

export function buildStartPointLocalizedPayload(
  values = {},
  languageList = [],
) {
  return (languageList || []).reduce((acc, language) => {
    const iso = language?.iso;
    if (!iso) return acc;
    acc[iso] = String(normalizeStartPointLocalizedValue(values?.[iso]) ?? "");
    return acc;
  }, {});
}

export function buildStartPointLocalizedTranslationPayload(
  values = {},
  languageList = [],
) {
  return (languageList || []).reduce((acc, language) => {
    const iso = language?.iso;
    if (!iso) return acc;
    const source = values?.[iso];
    acc[iso] = {
      id: source && typeof source === "object" ? source.id || "" : "",
      value: String(normalizeStartPointLocalizedValue(source) ?? ""),
    };
    return acc;
  }, {});
}

export function isPersistedStartPoint(point) {
  // Detects if this point already exists on backend to choose update vs create.
  return Boolean(
    point?.isPersisted || point?.internalId || point?.stp_id || point?.stpId,
  );
}

function normalizeAmount(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function normalizeDescription(value) {
  if (typeof value === "string") return value ? { es: value } : {};
  if (value && typeof value === "object") return value;
  return {};
}

function normalizeLocalizedField(value) {
  if (typeof value === "string") return value ? { en: value } : {};
  if (value && typeof value === "object") return value;
  return {};
}

function normalizeBanner(point = {}) {
  const source = point?.banner || point?.description?.banner || null;
  if (!source) return null;
  if (typeof source === "string") {
    return { id: source, url: "", mediaType: "image", mimeType: "" };
  }
  const rawUrl = source.url || "";
  return {
    id: source.id || "",
    url: resolveMediaUrl(rawUrl),
    mediaType: source.mediaType || inferMediaType(rawUrl) || "image",
    mimeType: source.mimeType || "",
  };
}

export function buildStartPointModel(point = {}, index = 0) {
  const longitude = Number(point.longitude ?? point.lng);
  const latitude = Number(point.latitude ?? point.lat);
  const normalizedTourTypes = Array.isArray(point?.tour_types)
    ? point.tour_types
    : point?.tour_types && typeof point.tour_types === "object"
      ? [point.tour_types]
      : typeof point?.tour_types === "string"
        ? [{ id: point.tour_types }]
        : [];
  const normalizedRouteTypes = Array.isArray(point?.routeTypes)
    ? point.routeTypes
    : point?.routeTypes && typeof point.routeTypes === "object"
      ? [point.routeTypes]
      : [];
  const fallbackTourTypeId =
    point?.tourTypeId ||
    point?.tour_type_id ||
    point?.tour_types_id ||
    point?.tourType?.id ||
    normalizedTourTypes?.[0]?.id ||
    normalizedRouteTypes?.[0]?.id ||
    "";
  return {
    id: point.id || point.stp_code || point.code || `point-${index + 1}`,
    uuid: point.uuid || point.internalId || point.stp_id || point.stpId || "",
    internalId:
      point.internalId || point.stp_id || point.stpId || point.uuid || "",
    isPersisted:
      typeof point.isPersisted === "boolean"
        ? point.isPersisted
        : Boolean(
            point.internalId || point.stp_id || point.stpId || point.uuid,
          ),
    order: Number(point.order) || index + 1,
    longitude: Number.isFinite(longitude) ? longitude : 0,
    latitude: Number.isFinite(latitude) ? latitude : 0,
    name: normalizeLocalizedField(
      point.name ||
        point.zoneName ||
        point.zone ||
        point.placeName ||
        point.locationName ||
        "",
    ),
    status: Number(point.status) || 1,
    isActive: typeof point.isActive === "boolean" ? point.isActive : true,
    description: normalizeDescription(point.description),
    banner:
      normalizeBanner(point) ||
      (point?.bannerId
      ? { id: point.bannerId, url: "", mediaType: "image", mimeType: "" }
      : null),
    additionalInfo: normalizeAdditionalInfo(point),
    price: {
      currencyIso: point?.price?.currencyIso || point?.currencyIso || "USD",
      amount: normalizeAmount(point?.price?.amount ?? point?.amount),
    },
    appleAppStoreId: String(
      point?.appleAppStoreId ??
        point?.AppleAppStoreID ??
        point?.apple_app_store_id ??
        "",
    ),
    googlePlayStoreId: String(
      point?.googlePlayStoreId ??
        point?.GooglePlayStoreID ??
        point?.google_play_store_id ??
        "",
    ),
    promoCodes: Array.isArray(point?.promoCodes)
      ? point.promoCodes
      : Array.isArray(point?.promo_codes)
        ? point.promo_codes
        : [],
    tour_types: normalizedTourTypes,
    routeTypes: normalizedRouteTypes,
    tourTypeId: String(fallbackTourTypeId || ""),
    modes: Array.isArray(point?.modes)
      ? point.modes
      : point?.modes && typeof point.modes === "object"
        ? [point.modes]
        : [],
  };
}

function normalizeAdditionalInfoImage(source = null) {
  if (!source) return null;
  if (typeof source === "string") {
    const url = resolveMediaUrl(source);
    return {
      id: "",
      url,
      mediaType: inferMediaType(url) || "image",
      mimeType: "",
    };
  }
  const rawUrl = source.url || source.image_url || "";
  return {
    id: source.id || source.mmd_id || "",
    url: resolveMediaUrl(rawUrl),
    mediaType: source.mediaType || inferMediaType(rawUrl) || "image",
    mimeType: source.mimeType || "",
  };
}

function normalizeAdditionalInfoImages(source = null) {
  const images = Array.isArray(source) ? source : source ? [source] : [];
  return images
    .map((image) => normalizeAdditionalInfoImage(image))
    .filter((image) => image && (image.id || image.url))
    .slice(0, 10);
}

function normalizeAdditionalInfo(point = {}) {
  const source =
    point?.additionalInfo ||
    point?.description_point ||
    point?.additional_info ||
    point?.more_information ||
    {};
  return {
    name: normalizeLocalizedField(source?.name || {}),
    description: normalizeDescription(source?.description || {}),
    image: normalizeAdditionalInfoImages(
      source?.image || source?.images || source?.image_url,
    ),
  };
}

export function syncStartPointModelsFromMap(currentPoints = [], mapPoints = []) {
  const safeCurrentPoints = Array.isArray(currentPoints) ? currentPoints : [];
  const safeMapPoints = Array.isArray(mapPoints) ? mapPoints : [];
  const mapPointById = new Map(
    safeMapPoints.map((point, index) => [
      String(point?.id || `point-${index + 1}`),
      point,
    ]),
  );

  const merged = safeCurrentPoints.map((point, index) => {
    const pointId = String(point?.id || `point-${index + 1}`);
    const mapPoint = mapPointById.get(pointId);
    if (!mapPoint) {
      return buildStartPointModel(
        {
          ...point,
          order: Number(point?.order) || index + 1,
        },
        index,
      );
    }
    return buildStartPointModel(
      {
        ...point,
        id: mapPoint.id || point.id || `point-${index + 1}`,
        order: mapPoint.order || point.order || index + 1,
        longitude: mapPoint.lng ?? mapPoint.longitude ?? point.longitude,
        latitude: mapPoint.lat ?? mapPoint.latitude ?? point.latitude,
        name:
          mapPoint.name ||
          mapPoint.zoneName ||
          mapPoint.zone ||
          point?.name ||
          "",
      },
      index,
    );
  });

  const existingIds = new Set(
    safeCurrentPoints.map((point, index) =>
      String(point?.id || `point-${index + 1}`),
    ),
  );
  const newPoints = safeMapPoints
    .filter((mapPoint, index) => {
      const mapPointId = String(mapPoint?.id || `point-${index + 1}`);
      return !existingIds.has(mapPointId);
    })
    .map((mapPoint, index) =>
      buildStartPointModel(
        {
          id: mapPoint?.id || `point-${safeCurrentPoints.length + index + 1}`,
          order:
            Number(mapPoint?.order) || safeCurrentPoints.length + index + 1,
          longitude: mapPoint?.lng ?? mapPoint?.longitude,
          latitude: mapPoint?.lat ?? mapPoint?.latitude,
          name: mapPoint?.name || mapPoint?.zoneName || mapPoint?.zone || "",
          status: 1,
          description: {},
          price: {
            currencyIso: "USD",
            amount: 0,
          },
          appleAppStoreId: "",
          googlePlayStoreId: "",
        },
        safeCurrentPoints.length + index,
      ),
    );

  return [...merged, ...newPoints].map((point, index) =>
    buildStartPointModel(
      {
        ...point,
        order: index + 1,
      },
      index,
    ),
  );
}
